import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { sleep } from '@cf-shared/shared-util';
import { SettingsService } from 'services/settings.service';
import { ToastController } from 'providers/toast-controller';
import { Toast } from 'components/toast/toast-host';
import { CsPage, NavController } from 'providers/nav-controller';
import { LoggingService } from 'services/logging.service';
import { animationFrame } from 'services/browser-utils';
import { CsInputComponent } from 'components/cs-input/cs-input';
import { IFoodsPage } from 'pages/foods/foods-interface';
import { BarcodeScannerPageParams, BarcodeScannerTab } from './barcode-scanner-interface';
import { triggerFeedback, Feedback } from 'services/haptics.service';
import { CameraInitResult, CameraService } from 'services/camera.service';
import { CsCameraViewComponent } from 'components/cs-camera-view/cs-camera-view';
import { BarcodeDetectionResult } from 'services/camera-strategy';
import { DEFAULT_BARCODE_FORMATS } from 'components/cs-barcode-detector/cs-barcode-detector.directive';
import { QuaggaProcessResult } from 'services/barcode-decoder.service';
import { ScannerName } from 'services/camera-permission-help';
import { BarcodeDetectionStability, NO_CODE_NUDGE_AFTER_SECS } from './barcode-detection-stability';

@Component({
    selector: 'page-barcode-scanner',
    templateUrl: 'barcode-scanner-page.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BarcodeScannerPage extends CsPage<BarcodeScannerPageParams> {
  protected ScannerName = ScannerName;
  protected LL = LL;
  // expose enum to template
  BarcodeScannerTab = BarcodeScannerTab;

  manualCodeInputCtrl: CsInputComponent;

  // needs this special setter because the control is dynamically shown and hidden
  @ViewChild('manualCodeInputCtrl') set content(content: CsInputComponent) {
    if (content) {
      // initially setter gets called with undefined
      this.manualCodeInputCtrl = content;
    }
  }

  @ViewChild('cameraView') cameraView: CsCameraViewComponent;

  /** True once `<cs-camera-view>` has finished initializing and emitted
   *  `(cameraReady)` at least once for the current mount. Used by the
   *  template to keep the camera holder's $gray-900 backdrop hidden and
   *  show a page-level spinner while the native plugin is still installing
   *  its preview layer + WebView mask — otherwise the user briefly sees a
   *  gray rectangle before the camera appears. Web path benefits too:
   *  the backdrop stays hidden until Quagga has attached its `<video>`. */
  cameraReady = false;

  /** Single CameraService the page owns and injects into `<cs-camera-view>`.
   *  One shared instance ensures `cameraPermissionDismissed` + `permissionStatus`
   *  state survives any unmount/remount of the viewport (the permission-denied
   *  template branch depends on this). */
  camera: CameraService;

  activeTab: BarcodeScannerTab = BarcodeScannerTab.Scan;

  /** Formats projected into `<cs-barcode-detector [types]>`. Defaults to the
   *  set Quagga decodes today. */
  barcodeFormats = DEFAULT_BARCODE_FORMATS;

  /** The rule that confirms a code and decides when to nudge the user towards
   *  better lighting or another camera. It only decides; the page acts. */
  private detection = new BarcodeDetectionStability();

  /** When the first decoder frame arrived (ms), or undefined. Read by the template. */
  get cameraStartedTime(): number {
    return this.detection.startedTime;
  }

  exiting: boolean = false; // needed to prevent racing between exiting and generating new detections
  manualCodeInput = '';

  private toastWaiting: Toast = null;
  private badCameraToast: Toast = null;

  constructor(
    private toastCtrl: ToastController,
    public settings: SettingsService,
    private navCtrl: NavController,
    logging: LoggingService,

    private cd: ChangeDetectorRef,
  ) {
    super();
    this.camera = new CameraService(settings, logging);

    // Page-owned handler fires only while cs-camera-view is unmounted — the
    // view save+restores this field across its own lifecycle. Matters after
    // a permission denial: the template `@if (!cameraPermissionDismissed...)`
    // removes the viewport, so a later re-grant has no view to react. Clearing
    // the flag + CD triggers the template to remount cs-camera-view, which
    // then re-runs init.
    this.camera.onPermissionChanged = () => {
      if (this.viewDestroyed) return;
      this.camera.cameraPermissionDismissed = false;
      this.cd.markForCheck();
    };
  }

  /** Forwarded from `<cs-camera-view>`'s `(permissionChange)` output. The
   *  viewport's own state-mutation path (inside cs-camera-view) ran on the
   *  same event; this handler only needs to trigger CD so the permission-
   *  denied / permission-granted template branches re-evaluate. Guarded
   *  against NG0911 via the standard page `viewDestroyed` check. */
  onPermissionChange() {
    if (this.viewDestroyed) return;
    // permission transitions tear down / re-init the camera view; mirror that
    // by dropping cameraReady so the page-level spinner comes back until the
    // next `(cameraReady)` emit
    this.cameraReady = false;
    this.cd.markForCheck();
  }

  /** Switch tabs. Resets `cameraReady` when re-entering Scan so the page
   *  shows the init spinner until cs-camera-view's fresh mount emits
   *  `(cameraReady)` again — otherwise the gray backdrop would flash in
   *  before the preview during the re-init window. Also auto-focuses the
   *  manual input when entering EnterManually so users can start typing
   *  immediately. */
  onTabChange(tab: BarcodeScannerTab) {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    if (tab === BarcodeScannerTab.Scan) {
      this.cameraReady = false;
    }
    if (tab === BarcodeScannerTab.EnterManually) {
      void (async () => {
        // wait one tick so the cs-input has mounted and its ViewChild
        // setter has populated manualCodeInputCtrl
        await sleep(0);
        this.manualCodeInputCtrl?.setFocus();
      })();
    }
    this.cd.markForCheck();
  }

  async returnResultAsync(code: string) {
    // if special navigation mode for day logging
    if (this.navParams.data.gotoFoodsAfterScan_dayDate) {
      // use replaceTopAsync to atomically replace BarcodeScannerPage with FoodsPage
      // (avoids race condition from popAsync + pushAsync)
      await this.navCtrl.replaceTopAsync(IFoodsPage, {
        mode: 'select',
        barcodeSearch: code,
        gotoLogFoodAfterSelect_dayDate: this.navParams.data.gotoFoodsAfterScan_dayDate
      });
      return;
    }
    // pass the code as result - NavController will invoke returnCallback automatically
    await this.navCtrl.popAsync(code);
  }

  // ── cs-camera-view outputs ────────────────────────────────────────────

  /** Fired by cs-camera-view whenever camera init succeeds (initial mount,
   *  user camera switch, rotation-loop retry recovery). Persist the freshly
   *  resolved `barcodeCameraId` / `barcodeCameraZoom` so the choice survives
   *  to the next session — pre-migration the page called `saveAsync()` here
   *  too, and subsequent saves ride on confirmed decodes / zoom changes. */
  onCameraReady() {
    // preview is actually running — reveal the camera backdrop + reticle and
    // hide the page-level init spinner
    this.cameraReady = true;
    this.cd.markForCheck();
    this.settings.saveInBackground('camera ready');
  }

  /** Fired once by cs-camera-view when its init loop can't open any camera.
   *  Surface the same toast the old in-page init path showed. */
  onCameraError(result: CameraInitResult) {
    // init failed — stay in the "not ready" state so the page doesn't flash
    // the empty camera backdrop behind the error toast
    this.cameraReady = false;
    if (result === CameraInitResult.NoCameraWorked &&
        this.camera.permissionStatus && this.camera.permissionStatus.state === 'granted') {
      this.toastCtrl.showToast({
        message: LL.barcodeScanner.messages.cannotInitializeCamera(),
        showCloseButton: true,
        duration: 10000,
      });
    }
    this.cd.markForCheck();
  }

  async ngAfterViewInit() {
    // cs-camera-view owns the camera lifecycle — it runs init via its own
    // AfterViewInit. Use the initial burst to dismiss the waiting toast when
    // the first frame comes through (onFrameProcessed resets cameraStartedTime).
    this.toastWaiting = this.toastCtrl.showToast({
      message: LL.barcodeScanner.messages.initializingCamera(),
      showCloseButton: false,
      duration: 10000,
    });
  }

  // ── Barcode detection handlers ────────────────────────────────────────

  /** Called by `<cs-barcode-detector>` when the decoder confirms a code. The
   *  page still applies a successive-match heuristic on top (see
   *  `BarcodeDetectionStability` + `(processed)`) because Quagga's per-detection
   *  output has a non-trivial false-positive rate — relying on
   *  `(detected)` alone would surface impostor codes. */
  onBarcodeDetected(_result: BarcodeDetectionResult) {
    // intentional no-op — confirmation counting runs inside onFrameProcessed
    // so we keep the identical heuristic that the pre-migration page applied.
    // See the comment on BarcodeDetectionStability for the rationale.
  }

  /** Per-frame raw decoder result. Drives (a) successive-match confirmation
   *  counting, (b) the "try flash / try zoom / switch cameras" hint toast,
   *  and (c) the custom detection overlay drawing (line, boxes) on Quagga's
   *  own overlay canvas. Empty on native strategies that don't run Quagga. */
  onFrameProcessed(result: QuaggaProcessResult) {
    if (this.toastWaiting) {
      this.toastCtrl.dismissToast(this.toastWaiting);
      this.toastWaiting = null;
    }
    // cameraStartedTime is seeded lazily on first frame so incorrect-detection
    // timing starts only once we actually have a stream going
    if (!this.detection.started) {
      this.detection.start(Date.now());
      // the rule only checks "no code for 10 s" when a frame arrives. Native
      // strategies send no frames while nothing is in view (only the start
      // seed and real detections), so feed one empty frame just past the
      // mark. Harmless on web: an empty frame never breaks a run, and the
      // hint shows at most once (#6360)
      this.safeTimeout(() => {
        // only while the camera is on screen: not on the manual-entry tab,
        // and not while the permission-denied help replaces it
        if (this.activeTab !== BarcodeScannerTab.Scan || this.camera.cameraPermissionDismissed) {
          return;
        }
        this.onFrameProcessed(undefined);
      }, NO_CODE_NUDGE_AFTER_SECS * 1000 + 500);
    }
    if (this.exiting) {
      return; // prevent racing — new detections could clear the counter after already found
    }

    const decision = this.detection.observeFrame(result, Date.now());
    if (decision.status === 'confirmed') {
      this.commitConfirmedCode(decision.code);
    } else if (decision.nudge) {
      void this.startBadCameraWarningsAsync();
    }
  }

  /** A code passed the confirmation rule: remember the working camera setup,
   *  then wrap up and leave the page. */
  private commitConfirmedCode(code: string) {
    let shouldSave = false;

    // once a code is found, we know the camera is good and we don't need to enumerate cameras again in the future
    if (!this.camera.savedCameraFunctional) {
      this.camera.savedCameraFunctional = true;
      shouldSave = true;
    }
    // on valid detections, remember the zoom as well (if different)
    if (this.camera.saveZoomIfChanged()) {
      shouldSave = true;
    }

    if (shouldSave) {
      this.settings.saveInBackground('scanner camera and zoom');
    }

    this.onCodeDetected(code);
  }


  // this handles audio-visual feedback and other wrapping up when a code is detected
  onCodeDetected(code: string) {
    console.log('Barcode: ', code);
    this.exiting = true;

    triggerFeedback(Feedback.ImpactMedium);
    triggerFeedback(Feedback.Beep);

    // Freeze the bracket-tracker overlay synchronously BEFORE the 500ms
    // confirmation freeze below — otherwise the hold timer (400ms) would
    // fire ~100ms into the freeze and morph the brackets back to white-
    // idle for ~100ms while the preview is frozen on the yellow detected
    // pose. No-op on native strategies (they have their own contract).
    this.cameraView?.freezeOverlay();

    void (async () => {
      // freeze the preview so the confirmation feels committed — matches the
      // pre-migration UX that applied `frameRate: 0` on the Quagga track.
      // No-op on native where there's no MediaStreamTrack (stopCamera below
      // handles teardown there instead).
      const track = this.cameraView?.getActiveTrack();
      if (track) {
        try {
          await track.applyConstraints({ frameRate: 0 });
        } catch (err) {
          // expected on drivers that reject frameRate:0 — preview just won't freeze
          console.log('Freeze via frameRate:0 refused by driver:', err);
        }
      }
      await sleep(500);
      // Tear down the camera BEFORE navigating away. On iOS native the
      // AVCapturePreviewLayer lives behind the WebView; without this stop
      // it stays visible through the WebView mask until cs-camera-view's
      // ngOnDestroy runs — which happens AFTER the route transition, so
      // the user sees the live preview on top of the incoming page. Await
      // is critical — the plugin.stop call is async and must finish before
      // the route transition starts.
      await this.cameraView?.stopCamera();
      await this.returnResultAsync(code);
    })();
  }


  /** Called at most once per scanner session — the detection rule latches its nudge. */
  async startBadCameraWarningsAsync() {
    // if the cameras are not listed yet, we don't know if the user even can switch, so enumerate them now
    if (this.camera.cameraInfos.length === 0) {
      await this.camera.listCamerasAsync();
    }

    if (this.badCameraToast) {
      return; // already showing
    }

    await animationFrame(); // wait for the icons to show up

    let possibleRemedies = [
      LL.barcodeScanner.remedies.betterLighting(),
      LL.barcodeScanner.remedies.differentDistance()
    ];
    if (this.camera.flashSupported && !this.camera.flashEnabled) {
      possibleRemedies.push(LL.barcodeScanner.remedies.enablingFlash());
    }
    if (this.camera.zoomLimits) {
      possibleRemedies.push(LL.barcodeScanner.remedies.zoomingInOut());
    }
    if (this.camera.cameraInfos.length > 1) {
      possibleRemedies.push(LL.barcodeScanner.remedies.switchingCameras());
    }

    const joinedRemedies = possibleRemedies.slice(0, -1).join(', ') + ' ' + LL.common.words.or() + ' ' + possibleRemedies[possibleRemedies.length - 1];
    let message = LL.barcodeScanner.messages.noBarcodeFound({ remedies: joinedRemedies });

    this.badCameraToast = this.toastCtrl.showToast({
      message: message,
      showCloseButton: true,
      duration: 30000,
      onClose: () => {
        this.badCameraToast = null;
      }
    });

  }

  async handleManualCodeConfirmed() {
    // if nothing is input
    if (this.manualCodeInput.length === 0) {
      return;
    }
    await this.returnResultAsync(this.manualCodeInput);
  }

  /** Hooked here (not in ionViewWillLeave) because cs-nav awaits
   *  ionViewCanLeave before starting the page transition, but fire-and-
   *  forgets ionViewWillLeave. Await the full round-trip — on native the
   *  AVCapturePreviewLayer only goes away after the Capacitor resolve
   *  comes back through the bridge, and anything less (fire-and-forget,
   *  rAF yield) leaves the preview visible on top of the incoming page
   *  during the transition animation. The ~300ms await cost is the price
   *  for a visually clean exit. */
  async ionViewCanLeave() {
    await this.cameraView?.stopCamera();
    return true;
  }

  async ionViewWillLeave() {
    this.exiting = true;
    if (this.toastWaiting) {
      this.toastCtrl.dismissToast(this.toastWaiting);
      this.toastWaiting = null;
    }
    if (this.badCameraToast) {
      this.toastCtrl.dismissToast(this.badCameraToast);
      this.badCameraToast = null;
    }
  }

}
